// services/saleng.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { SALENG_GREEN_API_PROD, SALENG_SHOP_API_PROD } from "./../config";

const SALENG_GREEN_API = SALENG_GREEN_API_PROD;
const SALENG_SHOP_API = SALENG_SHOP_API_PROD;

const getAccessToken = () => localStorage.getItem("accessToken") || null;

const decodeToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const getIds = () => {
  const decoded = decodeToken();
  return {
    accessToken: getAccessToken(),
    userId: decoded?.id ?? null,
    shopId: decoded?.shopId ?? null,
    branchId: decoded?.branchId ?? null,
    isMain: decoded?.isMain ?? false,
  };
};

/* ---------------- Auth ---------------- */
export const loginShop = (payload) =>
  axios.post(`${SALENG_SHOP_API}/auth/login`, payload, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Categories ---------------- */
export const getCategories = () => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/categories/${shopId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Products ---------------- */
export const getProductsBranch = (visible) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/data`, {
    params: { shopId, branchId, visible },
    headers: { "Content-Type": "application/json" },
  });
};

export const getProducts = () => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/${shopId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getProductGroupsAdjustments = (customerGroupId) =>
  axios.get(
    `${SALENG_SHOP_API}/products/adjustments/groups/${customerGroupId}`,
    {
      headers: { "Content-Type": "application/json" },
    }
  );

export const createProduct = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/products`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteProduct = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/products/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateProduct = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/products/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getPriceProduct = ({ productId, customerId }) => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/price`, {
    params: { productId, branchId, customerId },
    headers: { "Content-Type": "application/json" },
  });
};

export const getProductAdjustments = () => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/adjustments/${branchId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createProductAdjustment = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/products/adjustments/${branchId}`,
    { ...payload, shopId },
    { headers: { "Content-Type": "application/json" } }
  );
};

export const updateProductAdjustment = (productId, payload) => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.put(
    `${SALENG_SHOP_API}/products/adjustments/${branchId}/${productId}`,
    payload,
    { headers: { "Content-Type": "application/json" } }
  );
};

/* ---------------- Shop ---------------- */
export const getShop = (id) => {
  const { shopId } = getIds();
  const targetId = id ?? shopId;
  if (!targetId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/shop/${targetId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createShop = (payload, _shopId) => {
  const { shopId } = getIds();
  const finalShopId = _shopId ?? shopId;
  if (!finalShopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/shop`,
    { ...payload, shopId: finalShopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

/* ---------------- Branches ---------------- */
export const getBranch = (id) => {
  const { branchId } = getIds();
  const targetId = id ?? branchId;
  if (!targetId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/branches/${targetId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createBranch = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/branches`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteBranch = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/branches/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateBranch = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/branches/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Users ---------------- */
export const getUser = (id) =>
  axios.get(`${SALENG_GREEN_API}/users/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

export const createUser = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/users`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteUser = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/users/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateUser = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/users/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Transactions (Purchase) ---------------- */
export const getPurchaseTransactions = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/transaction/purchases-transaction`, {
    params: {
      shopId: params.shopId ?? shopId,
      branchId: params.branchId ?? branchId,
      ...params,
    },
    headers: { "Content-Type": "application/json" },
  });
};

export const getTransaction = (transactionId) =>
  axios.get(`${SALENG_SHOP_API}/transaction/${transactionId}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getTransactionByStatus = (status, startDate, endDate) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transaction/status`, {
    params: { status, shopId, branchId, startDate, endDate },
    headers: { "Content-Type": "application/json" },
  });
};

export const getBranchTransaction = () => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transaction/branch/${branchId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

const generateBillNumber = (branchCodeOrId, lastNumber) => {
  const today = new Date();
  const datePart = today.toISOString().slice(0, 10).replace(/-/g, "");
  const newNumber = String(lastNumber + 1).padStart(3, "0");
  const formattedBranch = String(branchCodeOrId).padStart(2, "0");
  return `B${formattedBranch}-${datePart}-${newNumber}`;
};

export const createTransaction = async (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");

  const transactionsResponse = await getBranchTransaction();
  const transactions = transactionsResponse?.data?.data ?? [];
  const billNumber = generateBillNumber(branchId, transactions.length);

  return axios.post(
    `${SALENG_SHOP_API}/transaction`,
    { ...payload, branchId, shopId, billNumber },
    { headers: { "Content-Type": "application/json" } }
  );
};

export const updateTransaction = (payload, id) =>
  axios.put(`${SALENG_SHOP_API}/transaction/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const deleteItemTransaction = (transactionId, id) =>
  axios.delete(`${SALENG_SHOP_API}/transaction/item/${transactionId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Transactions (Sale) ---------------- */
export const getSaleTransactions = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/sale/sale-transaction`, {
    params: {
      shopId: params.shopId ?? shopId,
      branchId: params.branchId ?? branchId,
      ...params,
    },
    headers: { "Content-Type": "application/json" },
  });
};

export const getSaleTransaction = (transactionId) =>
  axios.get(`${SALENG_SHOP_API}/sale/${transactionId}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getBranchSaleTransaction = () => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/sale/branch/${branchId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createSaleTransaction = async (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");

  const transactionsResponse = await getBranchSaleTransaction();
  const transactions = transactionsResponse?.data?.data ?? [];
  const billNumber = generateBillNumber(branchId, transactions.length);

  return axios.post(
    `${SALENG_SHOP_API}/sale`,
    { ...payload, branchId, shopId, billNumber },
    { headers: { "Content-Type": "application/json" } }
  );
};

export const updateSaleTransaction = (payload, id) =>
  axios.put(`${SALENG_SHOP_API}/sale/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const deleteItemSaleTransaction = (transactionId, id) =>
  axios.delete(`${SALENG_SHOP_API}/sale/item/${transactionId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getSaleTransactionByStatus = (status, startDate, endDate) => {
  const { shopId, branchId } = getIds();

  return axios.get(`${SALENG_SHOP_API}/sale/status`, {
    params: { status, shopId, branchId, startDate, endDate },
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Stock ---------------- */
export const updateStock = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/stock/inventory/receive/${shopId}/${branchId}`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const updateStockSell = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/stock/inventory/sell/${shopId}/${branchId}`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

/* ---------------- Inventory ---------------- */
export const getInventoryTransaction = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/stock/inventory-transaction`, {
    params: {
      shopId: params.shopId ?? shopId,
      branchId: params.branchId ?? branchId,
      ...params,
    },
    headers: { "Content-Type": "application/json" },
  });
};

export const getInventory = () => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/stock/inventory`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

export const getInventoryById = (id) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/stock/inventory/${id}`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Green API (users/points) ---------------- */
export const getUserByPhone = (phoneNumber) =>
  axios.get(`${SALENG_GREEN_API}/users/by-phone/${phoneNumber}`, {
    headers: { "Content-Type": "application/json" },
  });

export const addPointToUser = (id, body) =>
  axios.post(`${SALENG_GREEN_API}/users/${id}/add-point`, body, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Files ---------------- */
export const Upload = (formData) =>
  fetch(`${SALENG_SHOP_API}/files`, {
    method: "POST",
    body: formData,
  });

/* ---------------- Purchase Orders ---------------- */
export const getPurchaseOrders = (params) =>
  axios.get(`${SALENG_SHOP_API}/purchase-orders`, {
    params,
    headers: { "Content-Type": "application/json" },
  });

export const createPurchaseOrders = (payload) =>
  axios.post(`${SALENG_SHOP_API}/purchase-orders`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const updatePurchaseOrders = (id, payload) =>
  axios.put(`${SALENG_SHOP_API}/purchase-orders/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Sale Orders ---------------- */
export const getSaleOrders = (params) =>
  axios.get(`${SALENG_SHOP_API}/sale-orders`, {
    params,
    headers: { "Content-Type": "application/json" },
  });

export const createSaleOrders = (payload) =>
  axios.post(`${SALENG_SHOP_API}/sale-orders`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const updateSaleOrders = (id, payload) =>
  axios.put(`${SALENG_SHOP_API}/sale-orders/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Transform ---------------- */
export const getTransformTransaction = () => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transform`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

export const createTransformTransaction = (payload) => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId || !userId)
    return Promise.reject("Missing ids (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/transform`,
    { ...payload, transformBy: userId, shopId, branchId },
    { headers: { "Content-Type": "application/json" } }
  );
};

export const getCustomers = () => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/customer`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

// --- LockPricing (single) ---
export const upsertLockPricing = (transactionItemId, payload) => {
  return axios.put(
    `${SALENG_SHOP_API}/lock-pricing/${transactionItemId}`,
    payload,
    { headers: { "Content-Type": "application/json" } }
  );
};

// --- LockPricing (many) ---
export const upsertLockPricingMany = async (items) => {
  // items: [{ transactionItemId, productId, lockWeight, actualWeight, purchasePrice, predictPrice, status }]
  return Promise.all(
    items.map((it) =>
      upsertLockPricing(it.transactionItemId, {
        productId: it.productId,
        lockWeight: it.lockWeight ?? 0,
        actualWeight: it.actualWeight ?? 0,
        purchasePrice: it.purchasePrice ?? 0,
        predictPrice:
          it.predictPrice ?? (it.lockWeight ?? 0) * (it.purchasePrice ?? 0),
        status: it.status ?? "OPEN",
      })
    )
  );
};

/* ---------------- Pakeage ---------------- */

export async function getPackages() {
  const { shopId, branchId } = getIds();

  const { data } = await axios.get(`${SALENG_SHOP_API}/stock/package`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });

  return data?.data ?? data;
}

export async function fetchPackagesByProduct(productId) {
  const { shopId, branchId } = getIds();

  const { data } = await axios.get(`${SALENG_SHOP_API}/stock/package`, {
    params: { productId, shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });

  return data?.data ?? data;
}

export async function upsertPackage(payload) {
  const { shopId, branchId } = getIds();

  const { data } = await axios.post(
    `${SALENG_SHOP_API}/stock/package`,
    { ...payload, shopId, branchId },
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
}

export async function deletePackage(id) {
  const { data } = await axios.delete(
    `${SALENG_SHOP_API}/stock/package/${id}`,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return data;
}

export async function updatePurchaseOrderItemWeight(id, actualWeight) {
  return fetch(`${SALENG_SHOP_API}/purchase-orders/items/${id}/actual-weight`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ actualWeight }),
  });
}

export async function createLockPricing(payload) {
  const { isMain } = getIds();
  if (isMain) return;
  return fetch(`${SALENG_SHOP_API}/lock-pricing`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function updateLockPricing(productId, customerId, payload) {
  const { isMain } = getIds();
  if (!isMain) return;
  return fetch(`${SALENG_SHOP_API}/lock-pricing/${productId}/${customerId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/* ---------------- LockPricing ---------------- */

export async function getLockPricingByCustomer(customerId) {
  const { data } = await axios.get(`${SALENG_SHOP_API}/lock-pricing`, {
    params: { customerId },
  });
  return data;
}
/* ---------------- LockPricing ---------------- */
export const saveLayout = (payload) => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId) return Promise.reject("Missing shopId/branchId (token?)");
  const body = {
    ...payload,
    userId,
  };
  return axios.post(
    `${SALENG_SHOP_API}/products/${shopId}/layout`,
    body,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};


export const getUserLayout = async () => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  const res = await axios.get(`${SALENG_SHOP_API}/products/${shopId}/layout`, { params: { userId } });
  return res.data;
};
export const getBatches = async () => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  const res = await axios.get(`${SALENG_SHOP_API}/batches`, { params: { shopId, branchId } });
  return res.data;
};

export const updateBatch = (payload, id) =>
  axios.put(`${SALENG_SHOP_API}/batches/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
export const getTransactionNonBatch = (status, startDate, endDate) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transactions/unbatched`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};
export const createCustomBatch = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  const body = {
    ...payload,
    branchId,
    shopId,

  };
  return axios.post(`${SALENG_SHOP_API}/batches/custom-create`, body, {
    headers: { "Content-Type": "application/json" },
  });
};
export const editMaterial = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(`${SALENG_SHOP_API}/transaction/edit-material`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getAllBags = (transactionId) => {
  if (!transactionId)
    return Promise.reject("Missing transactionId");

  return axios.get(
    `${SALENG_SHOP_API}/transaction/all-bags/${transactionId}`,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const getBagsCustomers = (bagCodes, branchId) => {
  return axios.post(
    `${SALENG_SHOP_API}/transaction/bags-customers`,
    { bagCodes, branchId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};
